import type { ReactNode } from "react";
import { Card, DarkHero, EmptyState, PlatformIcon, StatDelta, type StatDeltaUnit, compactNumber } from "@cntrl/ui";
import type { MusicStreamingBlock, TrackStatView } from "../../app/hooks/useMusicStats.js";
import { ReleaseArtwork } from "../releases/ReleaseArtwork.js";
import type { RecentReleaseBlock } from "../releases/RecentReleaseCard.js";
import { formatShortDate, releaseTypeLabel } from "../releases/releaseFormat.js";
import styles from "./MusicMobile.module.scss";

// The phone layouts of the Streaming tab (Ryan, ART-190). `MusicScreen` picks these below `lg`, the way the
// Socials tab picks its mobile hero (ART-184), so the connect states, the data read and the region order stay
// in one screen and only the rendering forks.

type SpotifyHeroView = NonNullable<MusicStreamingBlock["spotify"]>;
type HeroDeltaView = NonNullable<SpotifyHeroView["monthlyListenersDelta"]>;

/** The mobile list's cap, the same ten the desktop table shows. */
const TOP_TRACKS_LIMIT = 10;

function Delta({ d, className }: { d: HeroDeltaView | null | undefined; className?: string }) {
  if (d?.value == null) return null;
  const unit: StatDeltaUnit = d.unit === "absolute" ? "absolute" : "percent";
  return <StatDelta value={d.value} unit={unit} tone="inverse" className={className} />;
}

/**
 * The mobile Spotify hero: monthly listeners as the headline with lifetime streams and popularity stacked
 * on the right, then a ruled 2x2 grid of streams · followers · playlist reach · editorial playlists. The
 * old app's phone layout, which Ryan asked for in place of the desktop 4x2 block. The "Playlists" tile is
 * dropped here ("drops some info"); its count is folded into the editorial cell's sub-line.
 *
 * Same rule as the desktop hero: a figure we do not hold is left out, never padded with a dash.
 */
export function MobileSpotifyHero({
  sp,
  photoUrl,
  action,
}: {
  sp: SpotifyHeroView;
  photoUrl: string | null;
  action?: ReactNode;
}) {
  const cells: { label: string; value: number; exact?: boolean; delta?: HeroDeltaView | null; sub?: string }[] = [];
  if (sp.streams28d != null) cells.push({ label: "Streams 28D", value: sp.streams28d, delta: sp.streams28dDelta });
  if (sp.followers != null) cells.push({ label: "Followers", value: sp.followers, delta: sp.followersDelta });
  if (sp.playlistReachTotal != null)
    cells.push({ label: "Playlist reach", value: sp.playlistReachTotal, sub: "sum of follower counts" });
  if (sp.playlistsEditorialCurrent != null)
    cells.push({
      label: "Editorial playlists",
      value: sp.playlistsEditorialCurrent,
      exact: true,
      sub: sp.playlistsTotal != null ? `${sp.playlistsTotal.toLocaleString()} playlists total` : undefined,
    });

  return (
    // The eyebrow lives INSIDE the lead column rather than in DarkHero's header slot, so the lifetime /
    // popularity column sits level with it as the lead's right-hand sibling instead of starting below it.
    <DarkHero photoUrl={photoUrl}>
      <div className={styles.heroTop}>
        <div className={styles.heroLead}>
          <span className={styles.heroEyebrow}>
            <PlatformIcon platform="spotify" size={16} />
            Monthly listeners
            {action}
          </span>
          <span className={styles.heroBig}>{sp.monthlyListeners != null ? compactNumber(sp.monthlyListeners) : "—"}</span>
          <Delta d={sp.monthlyListenersDelta} className={styles.heroLeadDelta} />
        </div>
        {(sp.streamsTotal != null || sp.popularity != null) && (
          <dl className={styles.heroSide}>
            {sp.streamsTotal != null && (
              <div>
                <dt>Lifetime str.</dt>
                <dd>{compactNumber(sp.streamsTotal)}</dd>
              </div>
            )}
            {sp.popularity != null && (
              <div>
                <dt>Popularity</dt>
                <dd>
                  {sp.popularity}
                  <span className={styles.heroSideSuffix}>/100</span>
                </dd>
              </div>
            )}
          </dl>
        )}
      </div>

      {cells.length > 0 && (
        <dl className={styles.heroGrid}>
          {cells.map((c) => (
            <div key={c.label} className={styles.heroCell}>
              <dt className={styles.heroCellLabel}>{c.label}</dt>
              <dd className={styles.heroCellValue}>{c.exact ? c.value.toLocaleString() : compactNumber(c.value)}</dd>
              {c.delta?.value != null && (
                <dd className={styles.heroCellDelta}>
                  <Delta d={c.delta} />
                </dd>
              )}
              {c.sub && <dd className={styles.heroCellSub}>{c.sub}</dd>}
            </div>
          ))}
        </dl>
      )}
    </DarkHero>
  );
}

/**
 * Latest release on mobile: the release's name under a "Latest release" eyebrow, then a card with the cover
 * beside a Live pill, the title and "Single · Released May 8, 2026", and the release's nine stats as a
 * ruled 3x3 grid. Ryan cut the "Release performance" label, the "Open" link and the upcoming pill from the phone
 * (ART-190). Like the desktop card, the grid keeps all its cells and dashes a figure we do not hold — it is
 * a fixed set of release stats, not the hero's variable-length row.
 */
export function MobileLatestRelease({ rr }: { rr: RecentReleaseBlock | null }) {
  const released = rr && !rr.isUpcoming;
  const sub = rr
    ? [
        releaseTypeLabel(rr.releaseType),
        rr.isUpcoming
          ? rr.releaseDate
            ? `Out ${formatShortDate(rr.releaseDate)}`
            : "Upcoming"
          : rr.releaseDate
            ? `Released ${formatShortDate(rr.releaseDate)}`
            : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : null;
  const st = rr?.stats;
  const stats: { label: string; value: string }[] = [
    { label: "Day in cycle", value: st?.dayInCycle != null ? `D${st.dayInCycle}` : "—" },
    { label: "Landing visits · MTD", value: fmt(st?.landingVisitsMtd) },
    { label: "Smart-Link · MTD", value: fmt(st?.smartLinkClicksMtd) },
    { label: "Spotify streams", value: fmt(st?.spotifyStreams) },
    { label: "Spotify popularity", value: st?.spotifyPopularity != null ? `${st.spotifyPopularity}/100` : "—" },
    { label: "Playlists · total", value: fmt(st?.playlists, true) },
    { label: "Playlist reach", value: fmt(st?.playlistReach) },
    { label: "Editorial picks", value: fmt(st?.editorialPicks, true) },
    // On a playlist right now, beside the all-time total above — the legacy card's ninth tile.
    { label: "Active playlists", value: fmt(st?.activePlaylists, true) },
  ];

  return (
    <section className={styles.region} aria-labelledby="music-release-heading">
      <div className={styles.releaseHead}>
        <h2 id="music-release-heading" className={styles.eyebrow}>
          Latest release
        </h2>
        <p className={styles.releaseHeadTitle}>{rr?.title ?? "No releases yet"}</p>
      </div>
      <Card>
        {rr ? (
          <>
            <div className={styles.release}>
              <ReleaseArtwork
                seed={rr.releaseId}
                mediaId={rr.coverMediaId}
                imageUrl={rr.coverImageUrl}
                size="tile"
                lazy={false}
                className={styles.releaseArt}
              />
              <div className={styles.releaseMeta}>
                {released && <span className={styles.livePill}>Live</span>}
                <p className={styles.releaseTitle}>{rr.title}</p>
                {sub && <p className={styles.releaseSub}>{sub}</p>}
              </div>
            </div>
            <dl className={styles.releaseStats}>
              {stats.map((x) => (
                <div key={x.label} className={styles.releaseStat}>
                  <dt className={styles.releaseStatLabel}>{x.label}</dt>
                  <dd className={styles.releaseStatValue} data-empty={x.value === "—" || undefined}>
                    {x.value}
                  </dd>
                </div>
              ))}
            </dl>
          </>
        ) : (
          <EmptyState title="No releases yet" description="Your latest release shows up here once you add one." />
        )}
      </Card>
    </section>
  );
}

/** A release stat: compact by default, exact for a small count, `—` when we hold nothing. */
function fmt(n: number | null | undefined, exact = false): string {
  if (n == null) return "—";
  return exact ? n.toLocaleString() : compactNumber(n);
}

/**
 * Top tracks on mobile: the cover where the desktop table has a rank number, the title over its release
 * date, then the popularity as "31 Pop" and lifetime streams beside the Spotify mark. No column header row
 * (ART-190). A figure we do not hold is dashed.
 */
export function MobileTopTracks({ tracks }: { tracks: TrackStatView[] }) {
  return (
    <Card title="Top tracks">
      <ol className={styles.tracks}>
        {tracks.slice(0, TOP_TRACKS_LIMIT).map((t, i) => (
          <li key={t.trackKey ?? i} className={styles.track}>
            <ReleaseArtwork
              size="chip"
              className={styles.trackArt}
              seed={t.title ?? t.trackKey ?? ""}
              mediaId={t.coverMediaId}
              imageUrl={t.coverImageUrl}
            />
            <span className={styles.trackBody}>
              <span className={styles.trackTitle}>{t.title ?? "Untitled"}</span>
              {t.releasedOn && <span className={styles.trackDate}>{formatShortDate(t.releasedOn)}</span>}
            </span>
            <span className={styles.trackPop}>
              {t.popularity != null ? t.popularity : "—"}
              <span className={styles.trackPopLabel}>Pop</span>
            </span>
            <span className={styles.trackStreams}>
              <span>{t.streamsLifetime != null ? compactNumber(t.streamsLifetime) : "—"}</span>
              <PlatformIcon platform="spotify" size={16} />
              <span className={styles.srOnly}>lifetime Spotify streams</span>
            </span>
          </li>
        ))}
      </ol>
    </Card>
  );
}
